import { useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Check, Flame, Lightbulb, Play } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const Phone = ({ children, bg = "bg-[#FAF9F6]", testId }) => (
  <div data-testid={testId} className="w-[248px] shrink-0 select-none">
    <div
      className={`overflow-hidden rounded-[2.4rem] border-[5px] border-[#1A1D1E] shadow-[0_10px_0_0_#1A1D1E] ${bg}`}
    >
      <div className="relative flex aspect-[9/19] flex-col px-4 pb-5 pt-10">
        <div
          className="absolute left-1/2 top-2.5 h-4 w-20 -translate-x-1/2 rounded-full bg-[#1A1D1E]"
          aria-hidden="true"
        />
        {children}
      </div>
    </div>
  </div>
);

const Chip = ({ children, color = "bg-[#FFC837]" }) => (
  <span
    className={`inline-block self-start rounded-full border-2 border-[#1A1D1E] ${color} px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#1A1D1E]`}
  >
    {children}
  </span>
);

const ScreenQuestion = () => (
  <Phone testId="screenshot-card-question">
    <Chip>Animals</Chip>
    <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full border-2 border-[#1A1D1E] bg-white">
      <div className="h-full w-[70%] bg-[#1FBCA2]" />
    </div>
    <p className="mt-5 font-display text-lg font-semibold leading-snug text-[#1A1D1E]">
      Which planet is the biggest in our solar system?
    </p>
    <div className="mt-auto flex flex-col gap-2.5">
      {["Mercury", "Jupiter", "Mars", "Venus"].map((a, i) => (
        <div
          key={a}
          className={`flex items-center justify-between rounded-[12px] border-2 border-[#1A1D1E] px-3.5 py-2.5 text-sm font-extrabold ${
            a === "Jupiter"
              ? "bg-[#1FBCA2] text-[#1A1D1E] shadow-[0_3px_0_0_#1A1D1E]"
              : "bg-white text-[#1A1D1E]"
          }`}
        >
          <span>
            <span className="mr-2 text-[#1A1D1E]/50">{"ABCD"[i]}</span>
            {a}
          </span>
          {a === "Jupiter" && <Check className="h-4 w-4" />}
        </div>
      ))}
    </div>
  </Phone>
);

const ScreenLightning = () => (
  <Phone testId="screenshot-card-lightning" bg="bg-[#FFC837]">
    <Chip color="bg-[#FF7B72]">Lightning round</Chip>
    <div className="mx-auto mt-8 grid h-28 w-28 place-items-center rounded-full border-4 border-[#1A1D1E] bg-white shadow-[0_5px_0_0_#1A1D1E]">
      <span className="font-display text-5xl font-bold text-[#1A1D1E]">10</span>
    </div>
    <p className="mt-5 flex items-center justify-center gap-1.5 font-display text-lg font-semibold text-[#1A1D1E]">
      <Flame className="h-5 w-5 fill-[#FF7B72] text-[#FF7B72]" />
      6 answer streak!
    </p>
    <div className="mt-auto grid grid-cols-2 gap-2.5">
      {["Sydney", "Perth", "Cairns", "Hobart"].map((a) => (
        <div
          key={a}
          className="rounded-[12px] border-2 border-[#1A1D1E] bg-white px-2 py-3 text-center text-xs font-extrabold text-[#1A1D1E]"
        >
          {a}
        </div>
      ))}
    </div>
  </Phone>
);

const ScreenFact = () => (
  <Phone testId="screenshot-card-fact" bg="bg-[#1FBCA2]">
    <Chip color="bg-[#FFC837]">Did you know?</Chip>
    <div className="mx-auto mt-8 grid h-24 w-24 place-items-center rounded-full border-4 border-[#1A1D1E] bg-white shadow-[0_5px_0_0_#1A1D1E]">
      <Lightbulb className="h-11 w-11 fill-[#FFC837] text-[#FFC837]" />
    </div>
    <p className="mt-6 text-center font-display text-lg font-semibold leading-snug text-[#1A1D1E]">
      Jupiter is so big, all the other planets could fit inside it – at the
      same time.
    </p>
    <p className="mt-3 text-center text-xs font-bold text-[#1A1D1E]/60">
      Every answer comes with extra info
    </p>
    <div className="mt-auto rounded-full border-2 border-[#1A1D1E] bg-white py-3 text-center font-display text-base font-semibold text-[#1A1D1E] shadow-[0_4px_0_0_#1A1D1E]">
      Got it!
    </div>
  </Phone>
);

const ScreenSound = () => (
  <Phone testId="screenshot-card-sound" bg="bg-white">
    <Chip color="bg-[#FF7B72]">Sound round</Chip>
    <p className="mt-5 font-display text-lg font-semibold leading-snug text-[#1A1D1E]">
      Which animal makes this sound?
    </p>
    <div className="mx-auto mt-6 grid h-24 w-24 place-items-center rounded-full border-2 border-[#1A1D1E] bg-[#FFC837] shadow-[0_5px_0_0_#1A1D1E]">
      <Play className="h-10 w-10 fill-[#1A1D1E] text-[#1A1D1E]" />
    </div>
    <div className="mt-6 flex items-end justify-center gap-1" aria-hidden="true">
      {[10, 24, 36, 18, 42, 28, 12, 32, 16].map((h, i) => (
        <span key={i} className="w-1.5 rounded-full bg-[#1FBCA2]" style={{ height: h }} />
      ))}
    </div>
    <div className="mt-auto grid grid-cols-2 gap-2.5">
      {["Koala", "Kookaburra", "Wombat", "Dingo"].map((a) => (
        <div
          key={a}
          className="rounded-[12px] border-2 border-[#1A1D1E] bg-[#FAF9F6] px-2 py-3 text-center text-xs font-extrabold text-[#1A1D1E]"
        >
          {a}
        </div>
      ))}
    </div>
  </Phone>
);

export const Screenshots = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", dragFree: true });
  const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <section data-testid="screenshots-section" className="overflow-hidden py-24 md:py-32">
      <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-6 px-6 md:px-12">
        <Reveal>
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#FF7B72]">
            Inside the app
          </p>
          <h2 className="mt-4 max-w-xl font-display text-4xl font-bold tracking-tight md:text-5xl">
            Real rounds.
            <span className="text-[#6B7280]"> Real learning.</span>
          </h2>
          <p className="mt-4 max-w-md leading-relaxed text-[#6B7280]">
            A peek at game night: age-matched questions, sound rounds,
            lightning rounds, and a fun fact after every answer.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="flex gap-3">
          <button
            onClick={prev}
            data-testid="screenshots-prev-button"
            aria-label="Previous screenshot"
            className="grid h-12 w-12 place-items-center rounded-full border-2 border-[#1A1D1E] bg-white shadow-[0_4px_0_0_#1A1D1E] transition-[transform,box-shadow] duration-150 hover:translate-y-[2px] hover:shadow-[0_2px_0_0_#1A1D1E] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FFC837]/60"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={next}
            data-testid="screenshots-next-button"
            aria-label="Next screenshot"
            className="grid h-12 w-12 place-items-center rounded-full border-2 border-[#1A1D1E] bg-[#FFC837] shadow-[0_4px_0_0_#1A1D1E] transition-[transform,box-shadow] duration-150 hover:translate-y-[2px] hover:shadow-[0_2px_0_0_#1A1D1E] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FFC837]/60"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="mt-14" ref={emblaRef}>
          <div className="flex gap-8 pl-6 md:pl-[max(3rem,calc((100vw-80rem)/2+3rem))]">
            <ScreenQuestion />
            <ScreenLightning />
            <ScreenFact />
            <ScreenSound />
          </div>
        </div>
      </Reveal>
    </section>
  );
};
